import React from "react";
import Seo from "../components/Seo";

const SEO_BY_SLUG = {
  privacy: {
    title: "Privacy Policy – DriveTerrain",
    description: "How DriveTerrain collects, uses and protects your personal information. Compliant with Canada's PIPEDA and provincial privacy laws.",
    keywords: "driveterrain privacy policy, pipeda compliance, canadian data privacy",
  },
  terms: {
    title: "Terms of Service – DriveTerrain",
    description: "The terms governing your use of DriveTerrain — for both learners booking driving lessons and instructors offering them across Canada.",
    keywords: "driveterrain terms of service, driving lesson terms, instructor marketplace terms",
  },
};

/**
 * Legal placeholder pages. Copy will be pasted in by the operator.
 * Reads Markdown-lite text from public assets (`/legal/{slug}.md`) if present,
 * otherwise renders a friendly placeholder.
 */
export default function LegalPage({ title, slug, lastUpdated }) {
  const [content, setContent] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    fetch(`/legal/${slug}.md`, { cache: "no-cache" })
      .then(r => r.ok ? r.text() : Promise.reject(new Error("not_found")))
      .then(t => setContent(t))
      .catch(e => setError(e.message));
  }, [slug]);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14" data-testid={`legal-page-${slug}`}>
      <Seo
        title={SEO_BY_SLUG[slug]?.title || `${title} – DriveTerrain`}
        description={SEO_BY_SLUG[slug]?.description || `${title} for DriveTerrain, Canada's marketplace for verified driving instructors.`}
        keywords={SEO_BY_SLUG[slug]?.keywords}
        canonical={`/${slug}`}
        ogType="article"
        twitterCard="summary"
      />
      <div className="text-xs uppercase tracking-[0.2em] font-bold text-primary" data-testid="legal-eyebrow">DriveTerrain · Legal</div>
      <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight mt-2" data-testid="legal-title">{title}</h1>
      {lastUpdated && (
        <div className="text-sm text-muted-foreground mt-2" data-testid="legal-updated">Last updated: {lastUpdated}</div>
      )}
      <div className="mt-8 prose prose-sm max-w-none">
        {content ? (
          <LegalMarkdown text={content} />
        ) : error ? (
          <PlaceholderBlock slug={slug} />
        ) : (
          <div className="text-sm text-muted-foreground animate-pulse">Loading…</div>
        )}
      </div>
      <div className="mt-14 pt-6 border-t border-border text-xs text-muted-foreground">
        Questions? Reach us at{" "}
        <a href="mailto:info@driveterrain.ca" className="underline text-primary">info@driveterrain.ca</a>.
      </div>
    </div>
  );
}

function PlaceholderBlock({ slug }) {
  return (
    <div className="border border-dashed border-border p-6 bg-muted/30" data-testid="legal-placeholder">
      <p className="text-sm">
        This {slug === "privacy" ? "Privacy Policy" : "Terms of Service"} page will be populated by the DriveTerrain team shortly.
      </p>
      <p className="text-xs text-muted-foreground mt-3">
        Operators: drop the finalized copy at <code className="text-xs">/app/frontend/public/legal/{slug}.md</code> (Markdown-lite). Redeploy to publish.
      </p>
    </div>
  );
}

/**
 * Extremely small Markdown-lite renderer — enough for legal pages without
 * pulling in a full parser: `# H1`, `## H2`, `### H3`, blank-line paragraphs,
 * bullet lists (`- `), bold (`**text**`), and email/URL autolinks.
 */
function LegalMarkdown({ text }) {
  const nodes = [];
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  let paraBuf = [];
  let listBuf = [];

  const flushPara = () => {
    if (paraBuf.length === 0) return;
    nodes.push(<p key={nodes.length} className="my-3 leading-relaxed">{renderInline(paraBuf.join(" "))}</p>);
    paraBuf = [];
  };
  const flushList = () => {
    if (listBuf.length === 0) return;
    nodes.push(
      <ul key={nodes.length} className="list-disc pl-6 my-3 space-y-1">
        {listBuf.map((li, i) => <li key={i}>{renderInline(li)}</li>)}
      </ul>
    );
    listBuf = [];
  };

  for (const raw of lines) {
    const l = raw.trim();
    if (l === "") { flushPara(); flushList(); continue; }
    if (l.startsWith("### ")) {
      flushPara(); flushList();
      nodes.push(<h3 key={nodes.length} className="font-semibold text-base mt-5 mb-2">{l.slice(4)}</h3>);
    } else if (l.startsWith("## ")) {
      flushPara(); flushList();
      nodes.push(<h2 key={nodes.length} className="font-display text-2xl font-semibold mt-8 mb-3">{l.slice(3)}</h2>);
    } else if (l.startsWith("# ")) {
      flushPara(); flushList();
      nodes.push(<h1 key={nodes.length} className="font-display text-3xl font-semibold mt-8 mb-3">{l.slice(2)}</h1>);
    } else if (l.startsWith("- ")) {
      flushPara();
      listBuf.push(l.slice(2));
    } else {
      flushList();
      paraBuf.push(l);
    }
  }
  flushPara(); flushList();

  return <>{nodes}</>;
}

function renderInline(s) {
  // Bold **x**
  const parts = [];
  let i = 0, key = 0;
  while (i < s.length) {
    const b = s.indexOf("**", i);
    if (b === -1) { parts.push(s.slice(i)); break; }
    parts.push(s.slice(i, b));
    const e = s.indexOf("**", b + 2);
    if (e === -1) { parts.push(s.slice(b)); break; }
    parts.push(<strong key={`b${key++}`}>{s.slice(b + 2, e)}</strong>);
    i = e + 2;
  }
  // Autolinks: apply per string part
  return parts.map((p, idx) => typeof p === "string" ? autoLink(p, `l${idx}`) : p);
}

function autoLink(text, keyBase) {
  const urlRe = /(https?:\/\/[^\s)]+|[\w.+-]+@[\w-]+\.[\w.-]+)/g;
  const out = [];
  let last = 0, m, k = 0;
  while ((m = urlRe.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const t = m[0];
    const href = t.startsWith("http") ? t : `mailto:${t}`;
    out.push(<a key={`${keyBase}-${k++}`} href={href} className="underline text-primary" target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer">{t}</a>);
    last = m.index + t.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}
